import React, { Fragment, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { observer, inject } from 'mobx-react';
import { isPast, format, addHours, roundToNearestMinutes } from 'date-fns';
import config from '../../../config/config';
import Text, { H3 } from '../../uikit/text/Text';
import TitleBlock from '../../shared/TitleBlock';
import Box from '../../uikit/box/Box';
import { Table } from '../../uikit/table';
import RatingDotLabel from '../../shared/rating/RatingDotLabel';
import Icon from '../../uikit/icon/Icon';
import {
  FiChevronUp,
  FiChevronDown,
  FiChevronLeft,
  FiPlus,
  FiEdit,
  FiMinus,
} from 'react-icons/fi';
import _groupBy from 'lodash/fp/groupBy';
import { getRatingLabel } from '../../../lib/utils';
import { computeMeetingRatings } from '../../../lib/meetingRatings';
import AverageLabel from '../../shared/average/AverageLabel';
import { ClampedText } from '../../shared/Collapsible';
import DimensionBreakdown from '../DimensionBreakdown';
import { getMeetingIntelligence, getMeetingHumanScore } from '../IntelligenceService';
const Reviewer = ({ review }) => {
  const [open, setOpen] = useState(false);

  return (
    <Box borderBottom="1px solid" borderColor="grays.1" py={2}>
      <Box display="flex" alignItems="center">
        <Text
          fontFamily="montserrat"
          mt="2px"
          flex="1 1 auto"
          minWidth={0}
          p={2}
          style={{ overflowWrap: 'anywhere' }}
        >
          {review.first_name} {review.last_name}
        </Text>
        <RatingDotLabel
          rating={review.rating}
          showRatingText={false}
          label={getRatingLabel(review.rating)}
          p={2}
          flex="0 0 132px"
        />
        <Icon
          p={2}
          color="primarys.0"
          icon={open ? FiMinus : FiPlus}
          onClick={() => {
            setOpen((s) => !s);
          }}
        />
      </Box>

      {open && (
        <Box border="1px solid" borderColor="grays.1" p={3} m={2}>
          <Text fontWeight="bold" mb={3}>
            Feedback:
          </Text>
          <Text fontFamily="montserrat">{review.feedback}</Text>
        </Box>
      )}
    </Box>
  );
};

const Attendee = ({ attendee }) => {
  const [open, setOpen] = useState(false);

  return (
    <Box
      boxShadow={2}
      my={5}
      p={3}
      minWidth="280px"
      minHeight="90px"
      position={'relative'}
      flex={1}
    >
      <Box display="flex" alignItems="center" height={'100%'}>
        {/*
          The name takes whatever is left and the rating gets a fixed slot, so
          every dot in a column lands on the same x. A percentage basis could not:
          a flex item will not shrink below its min-content width, and an
          unbroken address like manny.bartolini@coastalcoffee.com sets that width
          high enough to shove the rating across.
        */}
        <Box flex="1 1 auto" minWidth={0} p={2}>
          <H3
            mb={0}
            mt="2px"
            className="name"
            style={{
              maxHeight: '4rem',
              overflow: 'hidden',
              display: '-webkit-box',
              textOverflow: 'ellipsis',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflowWrap: 'anywhere',
            }}
          >
            {attendee.first_name ? attendee.first_name : ''} {attendee.last_name ? attendee.last_name : attendee.email_address}
          </H3>
          {/*
            Always render the line, even empty: a card without a job title would
            otherwise be shorter than its neighbours and break the row. The
            'undefined' guard is for rows where the API stored that string.
          */}
          <Text fontFamily="montserrat" fontSize={1}>
            {attendee.job_title && attendee.job_title !== 'undefined'
              ? attendee.job_title
              : ' '}
          </Text>
        </Box>
        <RatingDotLabel
          rating={attendee.average_rating}
          label="Average Rating"
          p={2}
          flex="0 0 172px"
        />
        <Icon
          p={2}
          color="primarys.0"
          icon={open ? FiChevronUp : FiChevronDown}
          onClick={() => {
            setOpen((s) => !s);
          }}
        />
      </Box>
      {open ? (
        <Box p={2}>
          {attendee.reviews.length > 0 ? (
            <Fragment>
              <Text
                p={2}
                fontFamily="montserrat"
                fontSize={0}
                textTransform="uppercase"
                fontWeight="bold"
                color="secondarys.0"
                borderBottom="1px solid"
                borderColor="grays.1"
              >
                Reviewed By
              </Text>
              {attendee.reviews.map((a, i) => (
                <Reviewer key={`${a.id}_${i}`} review={a} />
              ))}
            </Fragment>
          ) : (
            <Text color="accents.0" p={2}>
              No Reviews
            </Text>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

const MeetingDetail = ({ projectStore, match }) => {
  const [meeting, setMeeting] = useState(null);
  const [attendees, setAttendees] = useState(null);
  const [contract, setContract] = useState(null);
  const [intelligence, setIntelligence] = useState(null);
  const [humint, setHumint] = useState(null);

  useEffect(() => {
    fetch(`${config.API_URL}/meeting/${match.params.meetingId}`)
      .then((resp) => resp.json())
      .then((resp) => {
        setMeeting(resp.data.meeting);
      })
      .catch(console.log);
  }, []);

  useEffect(() => {
    // Loaded separately from the meeting: a meeting still being scored, or one
    // that never matched a recording, simply has no intelligence yet, and that
    // must not stop the rest of the page rendering.
    getMeetingIntelligence(match.params.meetingId)
      .then(setIntelligence)
      .catch(() => setIntelligence(null));
  }, []);

  useEffect(() => {
    getMeetingHumanScore(match.params.meetingId)
      .then(setHumint)
      .catch(() => setHumint(null));
  }, []);

  useEffect(() => {
    if (meeting) {
      const attendees = meeting.attendees.filter(
        (currentValue, currentIndex, array) => {
          return (
            array.findIndex(
              (a) => JSON.stringify(a) === JSON.stringify(currentValue)
            ) === currentIndex
          );
        }
      );
      setAttendees(_groupBy((x) => x.company_name, attendees));
      setContract(projectStore.meetingStore.meetings[0].contract_name);
    }
  }, [meeting]);
  console.log(attendees);
  if (!meeting) {
    return null;
  }

  const { humanRating, aiRating } = computeMeetingRatings({
    human_average: humint ? humint.human_average : null,
    ai_composite: intelligence ? intelligence.ai_composite : null,
  });

  return (
    <Box
      py={5}
      style={{
        minWidth: '500px',
      }}
    >
      <Box mb={5}>
        <Link
          to={`/project/${projectStore.project.id}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
          }}
        >
          <Icon
            icon={FiChevronLeft}
            style={{ transform: 'translateY(-1px)' }}
          />
          <Text>Back</Text>
        </Link>
      </Box>
      <H3 mb={6}>Meeting - {meeting.name}</H3>
      <Table>
        <thead>
          <tr>
            <th>
              <Text fontFamily="montserrat">Date</Text>
            </th>
            <th>
              <Text fontFamily="montserrat">Contract</Text>
            </th>
            <th>
              <Text fontFamily="montserrat">Project</Text>
            </th>
            <th>
              <Text fontFamily="montserrat">Meeting</Text>
            </th>
            <th>
              <Text fontFamily="montserrat">Progress</Text>
            </th>
            <th>
              <Text fontFamily="montserrat">Human Rating</Text>
            </th>
            <th>
              <Text fontFamily="montserrat">AI Rating</Text>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ borderBottom: meeting.summary ? 'none' : '' }}>
            <td className="date">
              <Text fontSize={1}>
                {format(new Date(meeting.start_date), 'MM.dd.yy')}
              </Text>
            </td>
            <td className="contract">
              <Text fontSize={1}>{contract}</Text>
            </td>
            <td className="project">
              <Text fontSize={1}>{projectStore.project.name}</Text>
            </td>
            <td className="meeting">
              <Text fontSize={1} fontWeight="bold">
                {meeting.name}
              </Text>
            </td>
            <td className="progress">
              <AverageLabel average={meeting.progress} />
            </td>
            <td>
              <RatingDotLabel rating={humanRating} />
              {/* The spec requires the response count wherever the score shows (§3.1). */}
              {humint && humint.respondent_count > 0 && (
                <Text fontSize={0} color="grays.2" mt={1}>
                  {humint.respondent_count} {humint.respondent_count === 1 ? 'response' : 'responses'}
                </Text>
              )}
            </td>
            <td>
              <RatingDotLabel rating={aiRating} />
            </td>
            {/*
            <td style={{ padding: 0 }} width="40px">
              {isPast(new Date(meeting.meeting_end_date)) ? (
                <Icon
                  color="primarys.0"
                  icon={FiChevronRight}
                  onClick={() => {
                    onRowClick && onRowClick(meeting);
                  }}
                />
              ) : (
                <Icon
                  color="primarys.0"
                  icon={FiEdit}
                  onClick={() => {
                    projectStore.meetingStore.setCurrentMeeting(meeting);
                    setStatus(STATUS.UPDATE);
                  }}
                />
              )}
            </td>
            */}
          </tr>
          {meeting.summary && (
            <tr>
              <td colSpan={7} style={{ paddingTop: 8, paddingBottom: 20, paddingRight: 24 }}>
                <Text
                  fontFamily="montserrat"
                  fontSize={0}
                  textTransform="uppercase"
                  fontWeight="bold"
                  color="secondarys.0"
                  mb={2}
                >
                  Summary
                </Text>
                {/*
                  Clamped: a long AI summary rendered in full pushes the attendee
                  reviews off screen. The toggle hides itself when the summary
                  already fits in four lines.
                */}
                {/*
                  Claude's summary is plain prose; the legacy MeetGeek one is a
                  small HTML document (headings, lists, transcript deep links), so
                  it goes through `html` and gets sanitized rather than escaped.
                */}
                {intelligence && intelligence.meeting_summary ? (
                  <ClampedText lines={4}>{intelligence.meeting_summary}</ClampedText>
                ) : (
                  <ClampedText lines={4} html={meeting.summary} />
                )}
              </td>
            </tr>)}
          {meeting.ai_insights && (
            <tr>
              <td colSpan={7} style={{ paddingTop: 8, paddingBottom: 20, paddingRight: 24 }}>
                <Text
                  fontFamily="montserrat"
                  fontSize={0}
                  textTransform="uppercase"
                  fontWeight="bold"
                  color="secondarys.0"
                  mb={2}
                >
                  Next Steps
                </Text>
                <ClampedText lines={4} html={meeting.ai_insights} />
              </td>
            </tr>)}
        </tbody>
      </Table>
      {intelligence && (
        <Box mt={4} mb={5}>
          <TitleBlock>Rating breakdown</TitleBlock>
          <Box px={3}>
            <DimensionBreakdown intelligence={intelligence} />
          </Box>
        </Box>
      )}

      <Box flex={1}>
        {/*
          One column per company, as many as fit. The previous fixed 48% basis
          assumed exactly two: three companies added up to 144% and ran off the
          right edge, and a single one sat in a half-empty row. auto-fit sizes
          the tracks instead of us guessing the count.
        */}
        <Box
          display="grid"
          style={{
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '16px',
            alignItems: 'start',
          }}
        >
          {attendees &&
            Object.keys(attendees).map((key, i) => {
              return (
                <Box minWidth={0} key={i}>
                  {/*
                    Fixed height so a company name that wraps to two lines does
                    not push its whole column down relative to the others.
                  */}
                  <TitleBlock
                    display="flex"
                    alignItems="center"
                    minHeight="68px"
                  >
                    {key} Attendee Reviews
                  </TitleBlock>
                  {attendees[key].length > 0 &&
                    attendees[key].map((attendee) => (
                      <Attendee key={attendee.id} attendee={attendee} />
                    ))}
                </Box>
              );
            })}
        </Box>
      </Box>
    </Box>
  );
};

export default inject('projectStore')(observer(MeetingDetail));
